import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { FragmentLineDto } from 'src/app/models/fragment-dto';
import { LanguageService } from 'src/app/services/language.service';

interface ProcessedLine {
  lineNumber: string;
  text: string;
  attributes: AttributeInfo[];
}

interface AttributeInfo {
  type: string;
  value: string;
  start: number;
  end: number;
  attributeString?: string;
  attributeValueString?: string;
}

@Component({
  selector: 'app-text-fragment',
  templateUrl: './text-fragment.component.html',
  styleUrls: ['./text-fragment.component.scss']
})
export class TextFragmentComponent implements OnInit, OnChanges {
  @Input() fragmentLines: FragmentLineDto[] = [];
  
  processedLines: ProcessedLine[] = [];
  currentLanguage!:string;
  constructor(
  ){}
  
  ngOnInit() {
    this.processFragmentLines();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fragmentLines']) {
      this.processFragmentLines();
    }
  }

  private processFragmentLines() {
    if (this.fragmentLines && this.fragmentLines.length > 0) {
      this.processedLines = this.fragmentLines.map((line: FragmentLineDto) => ({
        lineNumber: line.lineNumber || '1',
        text: line.text || '',
        attributes: this.parseAttributes(line.attributes)
      }));
    }
  }

  private parseAttributes(attributes: string | any[]): AttributeInfo[] {
    if (typeof attributes === 'string') {
      try {
        return JSON.parse(attributes);
      } catch (error) {
        console.error('Failed to parse attributes:', error);
        return [];
      }
    }
    return Array.isArray(attributes) ? attributes : [];
  }

  renderLineWithAttributes(line: ProcessedLine): string {
    if (!line.attributes || line.attributes.length === 0) {
      return this.escapeHtml(line.text);
    }

    let result = '';
    
    // Process each character position
    for (let i = 0; i < line.text.length; i++) {
      const char = line.text[i];
      
      // Check if character is reconstructed
      const isReconstructed = line.attributes.some((attr: AttributeInfo) => 
        attr.type === 'is_reconstructed' && 
        attr.value === 'TRUE' && 
        i >= attr.start && 
        i < attr.end
      );

      // Check for readability attributes
      const readabilityAttr = line.attributes.find((attr: AttributeInfo) => 
        attr.type === 'readability' &&
        i >= attr.start && 
        i < attr.end
      );

      // Build CSS classes
      let cssClasses = [];
      
      if (isReconstructed) {
        cssClasses.push('is-reconstructed-true');
      }

      if (readabilityAttr) {
        const readabilityValue = readabilityAttr.value;
        cssClasses.push(`readability-${readabilityValue.toLowerCase().replace(/_/g, '-')}`);
      }

      // Handle the character normally
      let displayChar = char === ' ' ? '&nbsp;' : this.escapeHtml(char);
      
      if (cssClasses.length > 0) {
        result += `<span class="${cssClasses.join(' ')}">${displayChar}</span>`;
      } else {
        result += displayChar;
      }
    }
    
    return result;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Helper method to check if line has reconstructed text
  hasReconstructedText(line: ProcessedLine): boolean {
    return line.attributes.some(attr => 
      (attr.attributeString === 'is_reconstructed' || attr.type === 'is_reconstructed') && 
      (attr.attributeValueString === 'TRUE' || attr.value === 'TRUE')
    );
  }

  // Helper method to get line CSS classes
  getLineClasses(line: ProcessedLine): string {
    const classes = ['line-text'];
    if (this.hasReconstructedText(line)) {
      classes.push('has-reconstructed');
    }
    return classes.join(' ');
  }

  // TrackBy function for better performance
  trackByLineNumber(index: number, line: ProcessedLine): string {
    return line.lineNumber;
  }
}